import { path, last, assocPath } from 'ramda';

export interface ITreeSection<T = any> {
  name: string;
  value: string;
  id: string;
  data?: T;
  open?: boolean;
  checked?: boolean;
  selected?: boolean;
  subList?: ITreeSection<T>[];
  disabled?: ITreeSectionDisabled;
  hidden?: boolean;
  duplicateNr?: number;
  indeterminate?: boolean;
  level?: number;
  parentNode?: ITreeSection<T>;
  isExpandable?: boolean;
}

export interface ITreeSectionDisabled {
  value: boolean;
  tooltip: string;
  labelSuffix?: string;
}

export enum SectionCheckStatus {
  NotChecked,
  PartiallyChecked,
  FullyChecked,
}

interface ICheckedUpdated<T = any> {
  updatedSections: ITreeSection<T>[];
  checkedSections: ICheckedItems<T>;
}

export interface ICheckedItems<T = any> {
  checkedItem: ITreeSection<T>;
  children: ITreeSection<T>[];
}

export const updateChecked = (
  updateSection: ITreeSection,
  sections: ITreeSection[],
  checked: boolean,
  suggestion?: ITreeSection
): ICheckedUpdated => {
  const checkedSections: ITreeSection[] = [];
  const section = getSection(sections, updateSection, suggestion);
  const updatee = last(
    section?.subList?.filter(x => x.value === suggestion?.value) ?? []
  );
  if (section) {
    if (updatee && updatee.duplicateNr) {
      section.subList?.splice(section?.subList.indexOf(updatee), 1);
    } else if (section.duplicateNr && !updatee) {
      const lastSection = last(sections.filter(x => x.value === section.value));
      if (lastSection) {
        sections.splice(sections.indexOf(lastSection), 1);
      }
    } else if (
      !section.subList ||
      (section.subList && section.subList.length === 0)
    ) {
      const toUpdate = updatee ?? section;
      checkedSections.push(toUpdate);
      toUpdate.checked = checked;
    } else {
      checkedSections.push(section);
      const update = (newSection: ITreeSection) => {
        if (newSection.value !== updateSection.value) {
          checkedSections.push(newSection);
        }
        newSection.checked = checked;
      };
      updateSubListTree(section, update);
    }
  }

  return {
    updatedSections: [...sections],
    checkedSections: { checkedItem: updateSection, children: checkedSections },
  };
};

export const updateOpen = (
  updateSection: ITreeSection,
  sections: ITreeSection[],
  suggestion?: ITreeSection
): ITreeSection[] => {
  const section = getSection(sections, updateSection, suggestion);
  const update = (toUpdate: ITreeSection) => (toUpdate.open = !section?.open);
  if (section) {
    update(section);
  }

  return [...sections];
};

export const updateOpenVirtualTree = (
  updateSection: ITreeSection,
  sections: ITreeSection[],
  suggestion?: ITreeSection
): ITreeSection[] => {
  const path: Array<string | number> = [];
  const item = findNodePathById(sections, updateSection.id, path);

  if (item) {
    const newSections = assocPath(
      path,
      { ...item, open: !item.open },
      sections
    );
    return [...newSections];
  }

  return sections;
};

export const getSublistCheckStatus = (
  section: ITreeSection,
  isChecked?: (section: ITreeSection) => SectionCheckStatus
): SectionCheckStatus => {
  let status = SectionCheckStatus.NotChecked;
  if ((section.checked || isChecked?.(section)) && !section.hidden) {
    status = SectionCheckStatus.FullyChecked;
  } else if (section.subList) {
    status = section.subList.reduce<SectionCheckStatus | null>((acc, x) => {
      const subStatus = getSublistCheckStatus(x);
      if (acc === null) {
        acc = subStatus;
      } else if (acc !== subStatus) {
        acc = SectionCheckStatus.PartiallyChecked;
      }

      return acc;
    }, null) as SectionCheckStatus;
  }

  return status === null ? SectionCheckStatus.NotChecked : status;
};

export const searchValueFilter = (
  sections: ITreeSection[],
  searchValue: string,
  isInitialStateOpen?: boolean | ((section: ITreeSection) => boolean)
): { sections: ITreeSection[]; anyMatch: boolean } => {
  const filtered = sections;
  if (!searchValue) {
    resetSectionsHidden(filtered, isInitialStateOpen);
    return { sections: [...filtered], anyMatch: true };
  }

  let anyMatch = false;
  const matches = (name: string) =>
    name.toLowerCase().trim().includes(searchValue.toLowerCase().trim());
  filtered.forEach(section => {
    if (section.subList) {
      const filterResult = searchValueFilter(section.subList, searchValue);
      section.subList = filterResult.sections;
      section.hidden = !filterResult.anyMatch;
      section.open = filterResult.anyMatch;
      if (filterResult.anyMatch) {
        anyMatch = true;
      }
    } else {
      const sectionMatch = matches(section.name);
      if (sectionMatch) {
        anyMatch = true;
      }
      section.hidden = !sectionMatch;
    }
  });

  return { sections: [...filtered], anyMatch };
};

const resetSectionsHidden = (
  sections: ITreeSection[],
  isInitialStateOpen?: boolean | ((section: ITreeSection) => boolean)
) => {
  sections.forEach(section => {
    section.hidden = false;
    section.open = isInitialStateOpen
      ? typeof isInitialStateOpen === 'function'
        ? isInitialStateOpen(section)
        : isInitialStateOpen
      : false;
    if (section.subList) {
      resetSectionsHidden(section.subList, isInitialStateOpen);
    }
  });
};

export const findNodePathById = (
  sections: ITreeSection[],
  id: string,
  path: Array<string | number>
): ITreeSection | null => {
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];

    if (section.id === id) {
      path.push(i);
      return section;
    } else if (section.subList && section.subList.length > 0) {
      path.push(i);
      path.push('subList');
      const maybeNode = findNodePathById(section.subList, id, path);
      if (maybeNode) {
        return maybeNode;
      } else {
        path.pop();
        path.pop();
      }
    }
  }

  return null;
};

export const getSection = (
  newSections: ITreeSection[],
  sectionValue: ITreeSection,
  suggestion?: ITreeSection
): ITreeSection => {
  const pathArray = sectionValue.value.split('|');
  let section =
    pathArray.length === 1
      ? newSections.find(
          x =>
            x.value === sectionValue.value &&
            x.duplicateNr === sectionValue.duplicateNr
        )
      : path<ITreeSection>(pathArray, newSections);
  if (suggestion) {
    section = section?.subList?.find(
      x =>
        x.value === suggestion.value && x.duplicateNr === suggestion.duplicateNr
    );
  }
  return section ?? suggestion ?? sectionValue;
};

const updateSubListTree = (
  section: ITreeSection,
  update: (section: ITreeSection) => void
) => {
  section.subList?.forEach(x => {
    if (x.subList) {
      updateSubListTree(x, update);
    } else {
      update(x);
    }
  });
};
